import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

const API = '/api/track';

function getSessionId() {
  let sid = sessionStorage.getItem('rp_sid');
  if (!sid) {
    sid = crypto.randomUUID ? crypto.randomUUID() : Date.now().toString(36) + Math.random().toString(36).slice(2);
    sessionStorage.setItem('rp_sid', sid);
  }
  return sid;
}

function getGclid() {
  // Check URL first (for fresh page loads)
  const params = new URLSearchParams(window.location.search);
  const gclid = params.get('gclid');
  if (gclid) {
    sessionStorage.setItem('rp_gclid', gclid);
    return gclid;
  }
  // Fall back to session-persisted value (captured early in index.html or on landing)
  return sessionStorage.getItem('rp_gclid') || null;
}

function getUtmParams() {
  const params = new URLSearchParams(window.location.search);
  const keys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
  const result = {};
  for (const key of keys) {
    const val = params.get(key);
    if (val) {
      sessionStorage.setItem('rp_' + key, val);
      result[key] = val;
    } else {
      result[key] = sessionStorage.getItem('rp_' + key) || null;
    }
  }
  return result;
}

function getConnectionType() {
  try {
    const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    return conn?.effectiveType || 'unknown';
  } catch { return 'unknown'; }
}

function incrementPageCount() {
  const count = parseInt(sessionStorage.getItem('rp_pages') || '0', 10) + 1;
  sessionStorage.setItem('rp_pages', count.toString());
  return count;
}

function getEntryPage() {
  let entry = sessionStorage.getItem('rp_entry');
  if (!entry) {
    entry = window.location.pathname;
    sessionStorage.setItem('rp_entry', entry);
  }
  return entry;
}

function sendBeacon(url, data) {
  const body = JSON.stringify(data);
  if (navigator.sendBeacon) {
    const blob = new Blob([body], { type: 'application/json' });
    navigator.sendBeacon(url, blob);
  } else {
    fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body, keepalive: true }).catch(() => {});
  }
}

export const Tracker = () => {
  const location = useLocation();
  const visitIdRef = useRef(null);
  const startTimeRef = useRef(Date.now());
  const maxScrollRef = useRef(0);
  const isFirstMount = useRef(true);

  // Track page visits on mount AND on each SPA navigation
  useEffect(() => {
    const sessionId = getSessionId();
    const gclid = getGclid();
    const utm = getUtmParams();
    const pagesInSession = incrementPageCount();
    const entryPage = getEntryPage();

    // On SPA navigation (not first mount), send duration for the previous page
    if (!isFirstMount.current && visitIdRef.current) {
      const duration = (Date.now() - startTimeRef.current) / 1000;
      sendBeacon(API + '/duration', {
        visit_id: visitIdRef.current,
        duration,
        scroll_depth: maxScrollRef.current,
        exit_page: location.pathname,
        pages_in_session: pagesInSession - 1,
      });
    }
    isFirstMount.current = false;

    // Reset metrics for the new page
    startTimeRef.current = Date.now();
    maxScrollRef.current = 0;

    // Record the new page visit
    fetch(API + '/visit', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        page: location.pathname,
        referrer: document.referrer,
        gclid,
        session_id: sessionId,
        screen_resolution: `${window.screen.width}x${window.screen.height}`,
        browser_language: navigator.language || '',
        connection_type: getConnectionType(),
        landing_url: window.location.href,
        entry_page: entryPage,
        pages_in_session: pagesInSession,
        ...utm,
      }),
    })
      .then(r => r.json())
      .then(data => { visitIdRef.current = data.visit_id; })
      .catch(() => {});
  }, [location.pathname]);

  // Global event listeners (scroll, click, form, unload) — set up once
  useEffect(() => {
    // Scroll depth tracking
    const handleScroll = () => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const docHeight = Math.max(document.documentElement.scrollHeight, document.body.scrollHeight) - window.innerHeight;
      if (docHeight > 0) {
        const pct = Math.min(100, Math.round((scrollTop / docHeight) * 100));
        if (pct > maxScrollRef.current) maxScrollRef.current = pct;
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });

    // Click tracking
    const handleClick = (e) => {
      const el = e.target.closest('a, button, [role="button"], input[type="submit"]');
      if (!el) return;

      const isTel = el.tagName === 'A' && el.href && el.href.startsWith('tel:');
      const isLink = el.tagName === 'A' && !isTel;
      const isButton = el.tagName === 'BUTTON' || el.getAttribute('role') === 'button';
      const isSubmit = el.tagName === 'INPUT' && el.type === 'submit';

      let clickType = 'other';
      if (isTel) clickType = 'phone_cta';
      else if (isSubmit) clickType = 'form_submit';
      else if (isLink) clickType = 'link';
      else if (isButton) clickType = 'button';

      const text = (el.textContent || el.value || '').trim().slice(0, 80);

      fetch(API + '/click', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          visit_id: visitIdRef.current || '',
          page: window.location.pathname,
          click_type: clickType,
          element_id: el.getAttribute('data-testid') || el.id || '',
          element_tag: el.tagName,
          element_text: text,
          gclid: getGclid(),
          session_id: getSessionId(),
        }),
      }).catch(() => {});
    };
    document.addEventListener('click', handleClick, true);

    // Form interaction tracking (quote form)
    const formFields = new Set();
    let formStartTime = 0;
    const handleFocus = (e) => {
      const input = e.target;
      if (!input.closest('#devis') && !input.closest('[data-testid*="input-"]')) return;
      const name = input.getAttribute('data-testid') || input.name || input.id || '';
      if (name) {
        if (formFields.size === 0) formStartTime = Date.now();
        formFields.add(name);
      }
    };
    document.addEventListener('focusin', handleFocus, true);

    // Send duration + scroll depth + exit page on leave
    const sendFinal = () => {
      if (visitIdRef.current) {
        const duration = (Date.now() - startTimeRef.current) / 1000;
        sendBeacon(API + '/duration', {
          visit_id: visitIdRef.current,
          duration,
          scroll_depth: maxScrollRef.current,
          exit_page: window.location.pathname,
          pages_in_session: parseInt(sessionStorage.getItem('rp_pages') || '1', 10),
        });
      }
      // Form abandon detection
      if (formFields.size > 0 && !sessionStorage.getItem('rp_form_submitted')) {
        const totalFields = 6;
        sendBeacon(API + '/form-event', {
          session_id: getSessionId(),
          page: window.location.pathname,
          event_type: 'abandon',
          fields_filled: formFields.size,
          total_fields: totalFields,
          time_spent: formStartTime > 0 ? (Date.now() - formStartTime) / 1000 : 0,
        });
      }
    };
    window.addEventListener('beforeunload', sendFinal);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') sendFinal();
    });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('click', handleClick, true);
      document.removeEventListener('focusin', handleFocus, true);
      window.removeEventListener('beforeunload', sendFinal);
    };
  }, []);

  return null;
};

// Form submission tracking (called from QuoteForm)
export function trackFormSubmission() {
  sessionStorage.setItem('rp_form_submitted', '1');
  const gclid = sessionStorage.getItem('rp_gclid') || null;
  const sessionId = sessionStorage.getItem('rp_sid') || '';
  fetch(API + '/click', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      visit_id: '', page: window.location.pathname, click_type: 'form_submit',
      element_id: 'submit-quote-btn', element_tag: 'BUTTON', element_text: 'Envoyer ma demande',
      gclid, session_id: sessionId,
    }),
  }).catch(() => {});
  fetch(API + '/form-event', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      session_id: sessionId, page: window.location.pathname, event_type: 'submit',
      fields_filled: 6, total_fields: 6, time_spent: 0,
    }),
  }).catch(() => {});
}

export default Tracker;
